/**
 * 轻量 ANSI 转义序列 → HTML 转换器（零依赖）
 * 覆盖：标准 16 色、256 色、加粗、暗淡、斜体、下划线、删除线、重置
 *
 * 颜色通过 CSS 变量输出（--sse-ansi-N），由 chat.css（深色）/ chat-theme-light.css（浅色）
 * 分别定义变量值，实现跟随主题变色。256 色立方和 truecolor 仍用直接 rgb()（它们是
 * 固定调色板，与主题无关，在浅/深底上都可读）。
 */

// 标准 16 色的 CSS 变量名映射（实际颜色由 CSS 按主题定义）
// 变量名约定：--sse-ansi-0 ~ --sse-ansi-15
const COLOR_VARS: string[] = [];
for (let i = 0; i <= 15; i++) {
  COLOR_VARS.push(`var(--sse-ansi-${i})`);
}

// 256 色调色板（0-15 用 CSS 变量；16-231 6×6×6 立方 + 232-255 灰阶用固定 rgb）
const PALETTE_256: string[] = (() => {
  const palette: string[] = [];
  // 0-15: 标准16色 → CSS 变量
  for (let i = 0; i <= 15; i++) palette.push(COLOR_VARS[i]);
  // 16-231: 6×6×6 RGB 立方（固定，与主题无关）
  const cube = [0, 95, 135, 175, 215, 255];
  for (let r = 0; r < 6; r++) {
    for (let g = 0; g < 6; g++) {
      for (let b = 0; b < 6; b++) {
        palette.push(`rgb(${cube[r]},${cube[g]},${cube[b]})`);
      }
    }
  }
  // 232-255: 24 级灰阶（固定）
  for (let i = 0; i < 24; i++) {
    const v = 8 + i * 10;
    palette.push(`rgb(${v},${v},${v})`);
  }
  return palette;
})();

interface StyleState {
  bold: boolean;
  dim: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  fg: string | null;
  bg: string | null;
}

function stateToStyles(s: StyleState): string[] {
  const styles: string[] = [];
  if (s.bold) styles.push('font-weight:bold');
  if (s.dim) styles.push('opacity:0.6');
  if (s.italic) styles.push('font-style:italic');
  if (s.underline) styles.push('text-decoration:underline');
  if (s.strikethrough) styles.push('text-decoration:line-through');
  if (s.underline && s.strikethrough) styles.push('text-decoration:underline line-through');
  if (s.fg) styles.push(`color:${s.fg}`);
  if (s.bg) styles.push(`background-color:${s.bg}`);
  return styles;
}

function applySgr(state: StyleState, codes: number[]): void {
  let i = 0;
  while (i < codes.length) {
    const code = codes[i];
    switch (code) {
      case 0: // 全部重置
        state.bold = false;
        state.dim = false;
        state.italic = false;
        state.underline = false;
        state.strikethrough = false;
        state.fg = null;
        state.bg = null;
        break;
      case 1: state.bold = true; break;
      case 2: state.dim = true; break;
      case 3: state.italic = true; break;
      case 4: state.underline = true; break;
      case 9: state.strikethrough = true; break;
      case 22: state.bold = false; state.dim = false; break;
      case 23: state.italic = false; break;
      case 24: state.underline = false; break;
      case 29: state.strikethrough = false; break;
      case 39: state.fg = null; break; // 默认前景色
      case 49: state.bg = null; break; // 默认背景色
      // 标准 16 色 — 前景
      case 30: case 31: case 32: case 33:
      case 34: case 35: case 36: case 37:
        state.fg = COLOR_VARS[code - 30];
        break;
      // 标准 16 色 — 亮色前景
      case 90: case 91: case 92: case 93:
      case 94: case 95: case 96: case 97:
        state.fg = COLOR_VARS[code - 90 + 8];
        break;
      // 标准 16 色 — 背景
      case 40: case 41: case 42: case 43:
      case 44: case 45: case 46: case 47:
        state.bg = COLOR_VARS[code - 40];
        break;
      // 标准 16 色 — 亮色背景
      case 100: case 101: case 102: case 103:
      case 104: case 105: case 106: case 107:
        state.bg = COLOR_VARS[code - 100 + 8];
        break;
      // 256 色 — 前景
      case 38:
        if (codes[i + 1] === 5 && codes[i + 2] != null) {
          state.fg = PALETTE_256[codes[i + 2]] || null;
          i += 2;
        } else if (codes[i + 1] === 2 && codes[i + 4] != null) {
          // truecolor: 38;2;r;g;b
          state.fg = `rgb(${codes[i + 2]},${codes[i + 3]},${codes[i + 4]})`;
          i += 4;
        }
        break;
      // 256 色 — 背景
      case 48:
        if (codes[i + 1] === 5 && codes[i + 2] != null) {
          state.bg = PALETTE_256[codes[i + 2]] || null;
          i += 2;
        } else if (codes[i + 1] === 2 && codes[i + 4] != null) {
          state.bg = `rgb(${codes[i + 2]},${codes[i + 3]},${codes[i + 4]})`;
          i += 4;
        }
        break;
      default:
        break;
    }
    i++;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * 将含 ANSI 转义序列的文本转为 HTML（带 <span> 内联样式）
 * 不含 ANSI 的纯文本会原样返回（仅 HTML 转义）
 *
 * 标准 16 色使用 CSS 变量（var(--sse-ansi-N)），颜色由主题 CSS 定义；
 * 256 色立方和 truecolor 使用固定 rgb()（与主题无关）。
 */
export function ansiToHtml(text: string): string {
  if (!text) return '';

  // 无 ANSI 转义序列时直接返回（快速路径）
  if (!text.includes('\x1b[')) return escapeHtml(text);

  const state: StyleState = {
    bold: false, dim: false, italic: false,
    underline: false, strikethrough: false,
    fg: null, bg: null,
  };

  let result = '';
  let lastIndex = 0;
  // 匹配 CSI 序列：ESC[ ... m（仅处理 SGR，即以 m 结尾的序列）
  const re = /\x1b\[([0-9;]*)m/g;
  let match: RegExpExecArray | null;
  let pendingText = '';

  const flushText = () => {
    if (!pendingText) return;
    const styles = stateToStyles(state);
    if (styles.length > 0) {
      result += `<span style="${styles.join(';')}">${escapeHtml(pendingText)}</span>`;
    } else {
      result += escapeHtml(pendingText);
    }
    pendingText = '';
  };

  while ((match = re.exec(text)) !== null) {
    // 累积转义序列之前的文本
    pendingText += text.slice(lastIndex, match.index);
    flushText();

    // 解析 SGR 参数
    const params = match[1];
    if (params === '' || params === '0') {
      // 重置
      applySgr(state, [0]);
    } else {
      const codes = params.split(';').map((s) => parseInt(s, 10) || 0);
      applySgr(state, codes);
    }

    lastIndex = re.lastIndex;
  }

  // 处理剩余文本
  pendingText += text.slice(lastIndex);
  flushText();

  return result;
}
